/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useQuery} from 'react-query';
import isNumber from 'lodash.isnumber';
import {getSubscription} from '@ion/core/src/services/Connect';
import {Subscription} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type useSubscriptionResult = UseDefaultQueryResult & {
  subscription: Subscription;
};

/**
 * The useSubscription hook provides the Query for referencing the Action Subscription.
 *
 * ```typescript
 *
 * import {useSubscription} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {useSubscription} = useSubscription();
 *
 * ```
 *
 * import {useSubscription} from '@ion/query/src/hooks/';
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns useSubscription containing the query results and the Action Subscription.
 */
export const useSubscription = (id: number, enabled = true): useSubscriptionResult => {
  const query = useQuery([QueryKey.ACCOUNT_SUBSCRIPTION, id], async() => {
    let results: Subscription;
    if (isNumber(id)) {
      results = await getSubscription(id);
    }
    return results;
  }, {staleTime: getStaleTime(60, false, false) as number, enabled});

  return createQueryResult(query, {
    subscription : query.data || {}
  }) as unknown as useSubscriptionResult;
};

export default useSubscription;
